import type { AddressSituation } from '@/lib/energyHistory'
import { NED_PROFILE_YEAR } from '@/lib/nedProfiles'
import { APP_STORE_URL, PLAY_STORE_URL } from '@/lib/branding'

export type DataQuality = 'goed' | 'redelijk' | 'beperkt'

/**
 * How trustworthy the advice will be, in plain language:
 * - goed:     nearly a full year of data, so all seasons are covered
 * - redelijk: 4-9 months, seasons are partly missing
 * - beperkt:  less than 4 months, or return without production data
 */
export function dataQuality(situation: AddressSituation): DataQuality {
  const missingProduction = situation.returnedKwh > 0 && situation.producedKwh == null
  if (situation.months >= 10 && !missingProduction) return 'goed'
  if (situation.months >= 4) return 'redelijk'
  return 'beperkt'
}

export const QUALITY_LABEL: Record<DataQuality, string> = {
  goed: 'Goed',
  redelijk: 'Redelijk',
  beperkt: 'Beperkt',
}

const QUALITY_STYLE: Record<DataQuality, string> = {
  goed: 'bg-light-green text-dark-blue',
  redelijk: 'bg-yellow/40 text-dark-blue',
  beperkt: 'bg-orange/20 text-dark-blue',
}

function qualityExplanation(quality: DataQuality, situation: AddressSituation): string {
  const monthsText =
    situation.months >= 12 ? 'een volledig jaar' : `${situation.months} ${situation.months === 1 ? 'maand' : 'maanden'}`
  switch (quality) {
    case 'goed':
      return `We hebben ${monthsText} aan meetgegevens van je huis. Daarmee zien we zomer én winter, dus het advies is betrouwbaar.`
    case 'redelijk':
      return `We hebben ${monthsText} aan meetgegevens van je huis. Dat is genoeg voor een goed beeld, maar we missen nog een deel van het jaar. Het echte resultaat kan daardoor iets anders uitpakken.`
    case 'beperkt':
      return situation.months > 0
        ? `We hebben pas ${monthsText} aan meetgegevens van je huis. Zie het advies daarom als een eerste indruk: hoe langer je meet, hoe preciezer het wordt.`
        : 'We hebben nog bijna geen meetgegevens van je huis. Zodra er een paar weken aan gegevens zijn, wordt het advies veel preciezer.'
  }
}

/**
 * Plain-language card about how much data we have (report quality) and,
 * when relevant, a CTA to connect more devices through the easyEnergy app.
 */
export function DataQualityCard({
  situation,
  compact = false,
  usedProfileCompletion = false,
}: {
  situation: AddressSituation
  compact?: boolean
  /** Economics were completed to a full year with the E1A AMI profile. */
  usedProfileCompletion?: boolean
}) {
  const quality = dataQuality(situation)
  const missingDevices: string[] = []
  if (situation.returnedKwh > 0 && !situation.hasSolar) missingDevices.push('je zonnepanelen-omvormer')
  if (situation.returnedKwh > 0 && !situation.hasBattery) missingDevices.push('je thuisbatterij (als je die hebt)')
  const showConnectCta = !compact && (quality !== 'goed' || missingDevices.length > 0)

  const badge = (
    <span
      className={`inline-flex h-8 shrink-0 items-center rounded-full px-4 text-13 font-semibold ${QUALITY_STYLE[quality]}`}
    >
      Kwaliteit van het advies: {QUALITY_LABEL[quality]}
    </span>
  )

  const profileNote = usedProfileCompletion ? (
    <p className="mt-2 max-w-3xl text-base leading-normal text-dark-blue/60">
      Jaarbasis geschat met het landelijke E1A AMI-verbruiksprofiel {NED_PROFILE_YEAR}{' '}
      (afname/teruglevering), omdat we nog geen volledig jaar meten. Seizoensgaten worden zo
      aangevuld in plaats van lineair opgeschaald.
    </p>
  ) : null

  if (compact) {
    return (
      <div className="chip w-full flex-wrap !justify-start !text-15 !font-normal">
        {badge}
        <span className="text-dark-blue/70">{qualityExplanation(quality, situation)}</span>
      </div>
    )
  }

  return (
    <div className="card-beige p-5 md:p-[30px]">
      <div className="flex flex-wrap items-center gap-3">{badge}</div>
      <p className="mt-3 max-w-3xl text-base leading-normal text-dark-blue">
        {qualityExplanation(quality, situation)}
      </p>
      {profileNote}
      {situation.earliest && (
        <p className="mt-2 max-w-3xl text-base leading-normal text-dark-blue/60">
          We meten op dit adres sinds{' '}
          {situation.earliest.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })}.
        </p>
      )}
      {showConnectCta && (
        <div className="mt-5 rounded-2xl bg-white p-4 md:p-5">
          <p className="text-base font-semibold text-dark-blue">
            Tip: verbind meer apparaten voor een beter advies
          </p>
          <p className="mt-1 max-w-3xl text-base leading-normal text-dark-blue/60">
            {missingDevices.length > 0 ? (
              <>
                We zien dat je stroom teruglevert, maar {missingDevices.join(' en ')} is nog niet
                gekoppeld.{' '}
              </>
            ) : null}
            Hoe meer apparaten je koppelt in de easyEnergy-app — zoals je zonnepanelen-omvormer,
            warmtepomp of laadpaal — hoe beter wij je huis begrijpen. En hoe beter dit advies
            wordt. Koppelen doe je in een paar minuten via de app.
          </p>
          <div className="mt-3 flex flex-wrap gap-2.5">
            <a href={APP_STORE_URL} target="_blank" rel="noreferrer" className="btn-secondary !px-4">
              Apple App Store
            </a>
            <a href={PLAY_STORE_URL} target="_blank" rel="noreferrer" className="btn-secondary !px-4">
              Google Play Store
            </a>
          </div>
        </div>
      )}
    </div>
  )
}
